/**
 * What makes an analytics model "active", and which UI surfaces each active model unlocks.
 *
 * Two questions, one answer, because they must agree:
 *   1. the Model Registry card's ACTIVE / INACTIVE pill
 *   2. whether a model's report shows up in the sidebar
 *
 * A model is active when it is actually running somewhere — i.e. at least one camera has it,
 * directly or inherited from a group. A registered-but-unattached model does nothing, so the
 * registry says INACTIVE and its report is hidden rather than offering a page that can only
 * ever be empty.
 *
 * Kept in the shared package (like REPORT_MODELS) so the local and cloud dashboards can't
 * disagree about which models are live.
 */
import type { Model } from '../api/contracts';

/**
 * Whether the model is switched on AND attached to at least one camera.
 *
 * `camera_count` is the node's EFFECTIVE count — direct + group-inherited cameras, see
 * modelService.listModels — so a model assigned only through a group still counts.
 * `is_active` / `status` still veto: a registry row that has been switched off is never
 * active, however it is attached.
 */
export function isModelActive(model: Model): boolean {
  if (model.is_active === false) return false;
  if (typeof model.status === 'string' && model.status.toLowerCase() !== 'active') return false;
  return (model.camera_count ?? 0) > 0;
}

/**
 * A model-gated UI surface — currently the per-model report nav entries.
 *
 * The registry stores only a free-text name (there is no model_type column), so a surface is
 * claimed by name, the same tolerant way modelIcon picks a card symbol and cameraIdsForModel
 * resolves attachments: deployments name the same model "person_counting", "Person Counting v2"
 * or "people counter" and all three must light up the Person Count report.
 *
 * `exclude` is what keeps the substrings honest — "person" is inside "person_counting", so
 * Person Detection has to disown anything that is really a counter.
 */
export interface ModelSurfaceRule {
  /** Lower-cased substrings; any hit claims the surface. */
  match: string[];
  /** Substrings that disqualify a name that otherwise matched. */
  exclude?: string[];
}

/**
 * surface key → naming rule. Keys are the report slugs used by the nav (`/reports/<slug>`)
 * so a nav entry gates itself with `requiresModel: '<slug>'`.
 */
export const MODEL_SURFACE_RULES: Record<string, ModelSurfaceRule> = {
  face: { match: ['face', 'facial'] },
  person: {
    match: ['person', 'people', 'human', 'pedestrian'],
    // "person_counting" is the Person Count surface, not this one.
    exclude: ['count', 'counting'],
  },
  'person-count': {
    match: ['person_count', 'person count', 'person-count', 'people count', 'occupancy', 'footfall', 'crowd'],
  },
  intrusion: { match: ['intrusion', 'trespass', 'perimeter'] },
  'vehicle-crossing': {
    match: ['vehicle', 'traffic', 'anpr', 'lpr', 'number plate', 'license plate', 'licence plate'],
  },
  'ppe-violation': { match: ['ppe', 'helmet', 'safety vest', 'safety_vest'] },
  theft: { match: ['theft', 'shoplift', 'pilfer'] },
  fall: { match: ['fall'] },
  // One surface for both classes: a registry model called "Smoke Detection" and one
  // called "Fire Detector" are the same detector and light up the same report.
  'fire-smoke': {
    match: ['fire', 'smoke', 'flame'],
    // "firearm" starts with "fire" and is a weapon detector, not a fire detector —
    // exactly the kind of collision `exclude` exists for.
    exclude: ['firearm'],
  },
  heatmap: { match: ['heatmap', 'heat map'] },
};

/**
 * The one surface a registry model name claims, or null when it claims none.
 *
 * The rules are written to be mutually exclusive (that is what `exclude` is for), so the
 * first hit is the answer. Used by the camera cards to turn a model chip into a link to
 * that model's report — every surface key except `heatmap` is also a `/reports/<slug>`.
 */
export function modelSurfaceForName(name: string | null | undefined): string | null {
  for (const surface of Object.keys(MODEL_SURFACE_RULES)) {
    if (modelNameMatchesSurface(name, surface)) return surface;
  }
  return null;
}

/** Whether one registry model name claims `surface`. */
export function modelNameMatchesSurface(name: string | null | undefined, surface: string): boolean {
  const rule = MODEL_SURFACE_RULES[surface];
  if (!rule) return false;
  const n = String(name || '').toLowerCase();
  if (!n) return false;
  if ((rule.exclude || []).some((x) => n.includes(x))) return false;
  return rule.match.some((m) => n.includes(m));
}

/**
 * Surfaces claimed by a set of model NAMES, with no activity test applied.
 *
 * For a caller that has already established which models are attached — the all-sites dashboard
 * derives them from the assignments payload it fetches anyway (a model_id appearing in
 * camera_assignments or group_assignments IS attached) — and so must not re-test `camera_count`,
 * a registry field that payload does not carry.
 *
 * The alternative was a second request per site purely to re-derive something already on hand,
 * which on an estate of ten sites is ten tunnel round trips every refresh for no new information.
 */
export function surfacesForModelNames(names: Array<string | null | undefined>): string[] {
  const surfaces = new Set<string>();
  for (const name of names) {
    for (const surface of Object.keys(MODEL_SURFACE_RULES)) {
      if (modelNameMatchesSurface(name, surface)) surfaces.add(surface);
    }
  }
  return [...surfaces].sort();
}

/**
 * The surfaces unlocked by a node's models — i.e. those claimed by an ACTIVE model.
 *
 * Pass the whole registry list; the activity test lives here so callers don't have to
 * remember that "active" means "attached to a camera".
 */
export function activeModelSurfaces(models: Model[] | null | undefined): Set<string> {
  const surfaces = new Set<string>();
  for (const model of models || []) {
    if (!isModelActive(model)) continue;
    for (const surface of Object.keys(MODEL_SURFACE_RULES)) {
      if (modelNameMatchesSurface(model.name, surface)) surfaces.add(surface);
    }
  }
  return surfaces;
}
